import { GroupedPresencesType } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/hooks/useBatchSelection';
import {
  GROUP_OPTION_VALUES,
  GroupingPresencesCriteria,
  PostLogoFor,
  PostPresenceFor,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import {
  GROUPING_CRITERIA,
  groupItemsByCriteria,
  groupItemsById,
  TGroupItemsByIdReturn,
} from '@/features/broadcast-schedule-program/helpers/group-by-id';
import { TClipLogo, TClipPeople } from '@/features/clips/types';
import { TLogo } from '@/features/logos/types';
import {
  LogoWithChildren,
  TParametersBBox,
  TSegmentLogo,
  TSegmentPeople,
} from '@/features/segments/types';
import { TPresenceItem } from '@/types/presences';

export const isString = (x: unknown): x is string => typeof x === 'string';

export const collectAllIds = <T extends { id?: string; children?: T[] }>(items: T[]): string[] =>
  items.flatMap(item =>
    item.children?.length
      ? item.children.map(child => child.id).filter(isString)
      : [item.id].filter(isString)
  );

export const getChildrenIds = (
  items: TGroupItemsByIdReturn<TSegmentLogo>,
  rootId: string
): string[] => {
  const item = items.find(i => i.id === rootId);
  if (!item?.children) return [];
  return item.children.map(child => child.id).filter(isString);
};

export const findById = (items: GroupedPresencesType, id: string) => {
  // 1. Search on root first
  const root = items.find(item => item.id === id);
  if (root) return root;

  // 2. Search on children
  for (const item of items) {
    if (!item.children) continue;

    const child = item.children.find(c => c.id === id);
    if (child) return child;
  }

  return undefined;
};

export const getLogoFromItem = <T extends TPresenceItem>(
  item: T,
  logosById: ReadonlyMap<string, TLogo>
) => {
  const logoId = 'logo_id' in item ? item.logo_id : item.id;

  return logoId ? logosById.get(logoId) : undefined;
};

export const buildNewPresenceDataToSave = <T extends TClipLogo | TSegmentLogo>(
  item: T
): PostLogoFor<T> => {
  const { parameters } = item;
  const newParameters: TParametersBBox = {
    bbox: parameters.bbox,
    landmarks: parameters.landmarks,
    segment_id: item?.segment_id,
    support_id: parameters.support_id,
    visibility: parameters.visibility,
  };

  const result = {
    id: item.id,
    segment_id: item.segment_id,
    entity_score: item.entity_score,
    start_datetime: new Date(item.start_datetime || ''),
    end_datetime: new Date(item.end_datetime || ''),
    parameters: newParameters,
    logo_id: item.logo_id,
    segment_logo_id: null,
    review_state: item.review_state,
  };

  return result as PostLogoFor<T>;
};

export const buildNewPeopleDataToSave = <T extends TSegmentPeople | TClipPeople>(
  item: T
): PostPresenceFor<T> => {
  const { parameters } = item;

  if ('segment_id' in item && !('clip_id' in item)) {
    const segmentItem = item as TSegmentPeople;
    const newParameters: TParametersBBox = {
      bbox: parameters.bbox,
      landmarks: parameters.landmarks,
      segment_id: segmentItem.segment_id,
      visibility: parameters.visibility,
    };
    return {
      id: item.id,
      segment_id: segmentItem.segment_id,
      entity_score: item.entity_score,
      start_datetime: new Date(item.start_datetime || ''),
      end_datetime: new Date(item.end_datetime || ''),
      parameters: newParameters,
      people_id: item.people_id,
      segment_people_id: item.id || '',
    } as unknown as PostPresenceFor<T>;
  }

  // TClipPeople
  const clipItem = item as TClipPeople;
  return {
    id: item.id,
    clip_id: clipItem.clip_id || '',
    people_id: item.people_id,
    entity_score: item.entity_score,
    start_datetime: new Date(item.start_datetime || ''),
    end_datetime: new Date(item.end_datetime || ''),
    review_state: item.review_state,
    parameters: { bbox: parameters.bbox, visibility: parameters.visibility },
  } as PostPresenceFor<T>;
};

export const itemHasChildren = <T extends TPresenceItem>(item: T): item is LogoWithChildren<T> =>
  !!item.children && item.children.length > 0;

export const itemHasBeenValidated = <T extends TPresenceItem>(item: T) => item.review_state !== 0;

export const groupPresences = <T extends TPresenceItem>(
  items: T[],
  groupCriteria: GroupingPresencesCriteria,
  entityIdKey: keyof T = 'logo_id' as keyof T
) => {
  switch (groupCriteria) {
    case GROUP_OPTION_VALUES.Support:
      return groupItemsByCriteria<T>(items, GROUPING_CRITERIA.Support);
    case GROUP_OPTION_VALUES.Visibility:
      return groupItemsByCriteria<T>(items, GROUPING_CRITERIA.Visibility);
    case GROUP_OPTION_VALUES.Reliability:
      return groupItemsByCriteria<T>(items, GROUPING_CRITERIA.Reliability);
    case GROUP_OPTION_VALUES.None:
      return items.map(item => ({ id: item[entityIdKey] ?? item.id, children: [item] }));
    case GROUP_OPTION_VALUES.Presence:
    default:
      return groupItemsById<T>({ items, idKey: entityIdKey });
  }
};
